import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Badge,
  Skeleton,
  Alert,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack
} from '@mui/material';
import {
  Search,
  ShoppingCart,
  Add,
  Remove,
  FilterList,
  Sort,
  LocalShipping,
  Inventory,
  Star
} from '@mui/icons-material';
import axios from 'axios';

const ProductCatalog = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [user, setUser] = useState(null);
  const productsPerPage = 12;

  // Get storeId from URL params for marketplace mode
  const getStoreId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('storeId');
  };

  // Get sheetId from authenticated user's business data (same logic as Auth.js)
  const getSheetId = () => {
    // If we're in marketplace mode (storeId present), don't require sheetId
    if (storeId) {
      console.log('🛍️ PRODUCT CATALOG: Marketplace mode - sheetId not required');
      return null;
    }

    // First check URL params for backward compatibility
    const urlParams = new URLSearchParams(window.location.search);
    const urlSheetId = urlParams.get('sheetId');
    if (urlSheetId) {
      console.log('🛍️ PRODUCT CATALOG: Using sheetId from URL:', urlSheetId);
      return urlSheetId;
    }

    // Then check authenticated user's business data
    if (user && user.business && user.business.sheetId) {
      console.log('🛍️ PRODUCT CATALOG: Using sheetId from user.business:', user.business.sheetId);
      return user.business.sheetId;
    }

    // Fallback to localStorage for backward compatibility
    const storedSheetId = localStorage.getItem('currentSheetId');
    if (storedSheetId) {
      console.log('🛍️ PRODUCT CATALOG: Using sheetId from localStorage:', storedSheetId);
      return storedSheetId;
    }

    // No fallback to master sheet - user must be authenticated with their own business
    console.log('🛍️ PRODUCT CATALOG: No sheetId found - user must authenticate first');
    return null;
  };

  // Get storeId from URL params for marketplace mode
  const storeId = getStoreId();
  // Get sheetId from authenticated user's business data (same logic as Auth.js)
  const sheetId = getSheetId();

  // Determine if user is a business owner (has business data)
  const isBusinessOwner = user && user.business && user.business.sheetId && !storeId;

  useEffect(() => {
    // Load user data from localStorage (check both business owner and customer auth)
    const businessToken = localStorage.getItem('authToken');
    const businessUserData = localStorage.getItem('userData');
    const customerToken = localStorage.getItem('customerToken');
    const customerUserData = localStorage.getItem('customerData');

    if (businessToken && businessUserData) {
      // Business owner authentication
      try {
        const parsedUser = JSON.parse(businessUserData);
        setUser(parsedUser);
        console.log('🛍️ PRODUCT CATALOG: Loaded business owner data:', parsedUser.name);
      } catch (error) {
        console.error('🛍️ PRODUCT CATALOG: Error parsing business user data:', error);
      }
    } else if (customerToken && customerUserData) {
      // Customer authentication
      try {
        const parsedUser = JSON.parse(customerUserData);
        setUser(parsedUser);
        console.log('🛍️ PRODUCT CATALOG: Loaded customer data:', parsedUser.name);
      } catch (error) {
        console.error('🛍️ PRODUCT CATALOG: Error parsing customer user data:', error);
      }
    } else {
      // No authentication found - this shouldn't happen since routes are now protected
      console.log('🛍️ PRODUCT CATALOG: No authentication data found - routes should be protected');
      setError('Please sign in to view products.');
      setLoading(false);
      return;
    }

    loadCart();
  }, [storeId]); // Re-load cart when storeId changes (navigation between modes)

  // Reload cart when user authentication state changes
  useEffect(() => {
    loadCart();
  }, [user, storeId]);

  // Separate effect for fetching products once user data is loaded
  useEffect(() => {
    if (storeId) {
      // Single store marketplace mode
      fetchStoreProducts();
    } else if (user && user.business) {
      // Business owner mode - show their own products
      fetchProducts();
    } else if (user && !storeId) {
      // Customer mode - show all marketplace products
      fetchAllMarketplaceProducts();
    }
  }, [user, storeId, sheetId]);

  // Reload cart when user authentication state changes
  useEffect(() => {
    loadCart();
  }, [user, storeId]);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, searchTerm, selectedCategory, sortBy, sortOrder]);

  // Reload cart when localStorage changes (e.g., after checkout clears cart)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key && (e.key.startsWith('cart_') || e.key === 'authToken' || e.key === 'userData' || e.key === 'customerToken' || e.key === 'customerData')) {
        console.log('🛍️ PRODUCT CATALOG: Storage changed for key:', e.key, '- reloading cart');
        loadCart();
      }
    };

    // Listen for custom cart update events (same-tab synchronization)
    const handleCartUpdate = (e) => {
      console.log('🛍️ PRODUCT CATALOG: Custom cart update event received:', e.detail);
      // Only reload if it's a different cart key than what we're currently using
      const currentCartKey = getCurrentCartKey();
      if (e.detail.cartKey !== currentCartKey) {
        loadCart();
      }
    };

    // Listen for storage events (cross-tab synchronization)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cartUpdated', handleCartUpdate);

    // Also reload cart when component gains focus (user returns to tab)
    const handleFocus = () => {
      console.log('🛍️ PRODUCT CATALOG: Window focused, reloading cart');
      loadCart();
    };
    window.addEventListener('focus', handleFocus);

    // Also reload cart when page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🛍️ PRODUCT CATALOG: Page became visible, reloading cart');
        loadCart();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, storeId]); // Add dependencies to ensure proper re-binding

  const ensureBusinessInitialized = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_API_URL;
      const token = localStorage.getItem('authToken');

      if (!token || !user || !user.business) {
        console.log('🛍️ PRODUCT CATALOG: No authentication or business data available');
        return false;
      }

      // Check if business is already initialized by trying to get business profile
      try {
        const profileResponse = await axios.get(`${backendUrl}/api/business/profile/${user.id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (profileResponse.data.success) {
          console.log('🛍️ PRODUCT CATALOG: Business already initialized');
          return true;
        }
      } catch (profileError) {
        console.log('🛍️ PRODUCT CATALOG: Business not initialized, initializing now...');
      }

      // Initialize the business
      const initResponse = await axios.post(`${backendUrl}/business/initialize`, {
        sheetId: user.business.sheetId,
        businessName: user.business.name || `Business-${user.business.sheetId.substring(0, 8)}`,
        userEmail: user.email,
      }, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (initResponse.data.success) {
        console.log('🛍️ PRODUCT CATALOG: Business initialized successfully');
        return true;
      } else {
        console.error('🛍️ PRODUCT CATALOG: Business initialization failed');
        return false;
      }
    } catch (error) {
      console.error('🛍️ PRODUCT CATALOG: Error initializing business:', error);
      return false;
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store/api';

      // Check if we have a valid sheetId (for business owner mode)
      if (!sheetId && !storeId) {
        setError('Please sign in to view your products.');
        console.log('🛍️ PRODUCT CATALOG: No sheetId or storeId available');
        setLoading(false);
        return;
      }

      // For business owner mode, ensure business is initialized
      if (sheetId && !storeId) {
        const businessInitialized = await ensureBusinessInitialized();
        if (!businessInitialized) {
          setError('Failed to initialize your business. Please try signing in again.');
          setLoading(false);
          return;
        }
      }

      // Get the Google access token from localStorage (same as Auth.js)
      const token = localStorage.getItem('authToken');

      // Request headers with Google access token for private sheet access (only for business owners)
      const headers = (token && sheetId) ? { Authorization: `Bearer ${token}` } : {};

      // Log what we're fetching
      if (storeId) {
        console.log('🛍️ PRODUCT CATALOG: Fetching marketplace products for storeId:', storeId);
      } else {
        console.log('🛍️ PRODUCT CATALOG: Fetching products for sheetId:', sheetId);
      }

      // Choose the appropriate API endpoint
      const apiUrl = storeId
        ? `${backendUrl}/marketplace/products?storeId=${storeId}`
        : `${backendUrl}/ecommerce/products?sheetId=${sheetId}`;

      const response = await axios.get(apiUrl, { headers });
      console.log('🛍️ PRODUCT CATALOG: API Response received');
      console.log('🛍️ PRODUCT CATALOG: Response status:', response.status);
      console.log('🛍️ PRODUCT CATALOG: Response data keys:', Object.keys(response.data));
      console.log('🛍️ PRODUCT CATALOG: Response success:', response.data.success);
      console.log('🛍️ PRODUCT CATALOG: Products count:', response.data.products ? response.data.products.length : 'undefined');

      if (response.data.success && response.data.products) {
        setProducts(response.data.products);
        setTotalPages(Math.ceil(response.data.products.length / productsPerPage));
        console.log('🛍️ PRODUCT CATALOG: Successfully loaded', response.data.products.length, 'products');

        // If no products found, show a helpful message
        if (response.data.products.length === 0) {
          if (storeId) {
            console.log('🛍️ PRODUCT CATALOG: No products found in this store');
          } else {
            console.log('🛍️ PRODUCT CATALOG: No products found - check if Google Sheets has data in Purchase/Inventory sheets');
          }
        }
      } else {
        console.error('🛍️ PRODUCT CATALOG: API response issue:', {
          success: response.data.success,
          hasProducts: !!response.data.products,
          productsLength: response.data.products ? response.data.products.length : 'N/A',
          fullResponse: response.data
        });
        setError('Failed to load products. The server returned an unexpected response.');
      }
    } catch (err) {
      setError('Failed to load products. Please try again.');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreProducts = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store/api';
      const response = await axios.get(`${backendUrl}/marketplace/products?storeId=${storeId}`);
      if (response.data.success) {
        setProducts(response.data.products);
        setTotalPages(Math.ceil(response.data.products.length / productsPerPage));
      }
    } catch (err) {
      setError('Failed to load store products. Please try again.');
      console.error('Error fetching store products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllMarketplaceProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store/api';
      const response = await axios.get(`${backendUrl}/marketplace/products`);
      if (response.data.success) {
        setProducts(response.data.products);
        setTotalPages(Math.ceil(response.data.products.length / productsPerPage));
        console.log('🛍️ PRODUCT CATALOG: Loaded', response.data.products.length, 'products from all stores');
      } else {
        setError('Failed to load marketplace products.');
      }
    } catch (err) {
      setError('Failed to load marketplace products. Please try again.');
      console.error('Error fetching marketplace products:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortProducts = () => {
    let filtered = [...products];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(product =>
        (product.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.sku || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'price':
          aValue = a.price || a.sellingPrice || 0;
          bValue = b.price || b.sellingPrice || 0;
          break;
        case 'stock':
          aValue = a.stock || a.quantityAvailable || 0;
          bValue = b.stock || b.quantityAvailable || 0;
          break;
        case 'trending':
          // Sort by order count or view count (higher = more trending)
          aValue = a.orderCount || a.viewCount || 0;
          bValue = b.orderCount || b.viewCount || 0;
          break;
        case 'rating':
          // Sort by product rating (higher = better rated)
          aValue = a.rating || a.averageRating || 0;
          bValue = b.rating || b.averageRating || 0;
          break;
        case 'recent':
          // Sort by creation date (newer = more recent)
          aValue = new Date(a.createdAt || a.dateAdded || 0);
          bValue = new Date(b.createdAt || b.dateAdded || 0);
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredProducts(filtered);
    setTotalPages(Math.ceil(filtered.length / productsPerPage));
    setPage(1); // Reset to first page when filters change
  };

  const loadCart = async () => {
    if (user && user.id) {
      // Authenticated customer - load from database
      console.log('🛍️ PRODUCT CATALOG: Loading cart from database for customer:', user.id);

      try {
        const token = localStorage.getItem('customerToken');
        if (!token) {
          console.log('🛍️ PRODUCT CATALOG: No auth token found, cannot load from database');
          setCart([]);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/cart`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            console.log('🛍️ PRODUCT CATALOG: Successfully loaded cart from database:', result.cart.length, 'items');
            const cartData = Array.isArray(result.cart) ? result.cart : [];
            setCart(cartData);
            // Also save to localStorage for consistency
            const cartKey = getCurrentCartKey();
            localStorage.setItem(cartKey, JSON.stringify(cartData));
          } else {
            console.error('🛍️ PRODUCT CATALOG: Failed to load cart from database:', result.message);
            setCart([]);
          }
        } else {
          console.error('🛍️ PRODUCT CATALOG: Database cart load failed with status:', response.status);
          setCart([]);
        }
      } catch (dbError) {
        console.error('🛍️ PRODUCT CATALOG: Error loading cart from database:', dbError);
        // Fallback to localStorage
        loadCartFromLocalStorage();
      }
    } else {
      // Unauthenticated user - load from localStorage
      console.log('🛍️ PRODUCT CATALOG: Loading cart from localStorage for unauthenticated user');
      loadCartFromLocalStorage();
    }
  };

  const loadCartFromLocalStorage = () => {
    // Use customer-specific cart key for authenticated customers
    // This ensures cart persists across sessions and different store visits
    let cartKey;
    if (user && user.id) {
      // Authenticated customer - use customer ID for cart persistence
      cartKey = `cart_customer_${user.id}`;
    } else if (storeId) {
      // Single store mode for unauthenticated users
      cartKey = `cart_store_${storeId}`;
    } else {
      // Full marketplace mode for unauthenticated users
      cartKey = 'cart_marketplace';
    }

    console.log('🛍️ PRODUCT CATALOG: Loading cart from key:', cartKey);

    const savedCart = localStorage.getItem(cartKey);
    if (savedCart && savedCart !== 'undefined' && savedCart !== 'null') {
      try {
        const parsedCart = JSON.parse(savedCart);
        console.log('🛍️ PRODUCT CATALOG: Loaded cart with items:', parsedCart?.length || 0);
        setCart(Array.isArray(parsedCart) ? parsedCart : []);
      } catch (error) {
        console.error('🛍️ PRODUCT CATALOG: Error parsing cart data:', error);
        setCart([]);
      }
    } else {
      console.log('🛍️ PRODUCT CATALOG: No saved cart found');
      setCart([]);
    }
  };

  const saveCart = async (newCart) => {
    setCart(newCart);

    // For authenticated users, cart is saved via API calls in addToCart/updateCartQuantity/removeFromCart
    // This function is mainly for local state management and fallback to localStorage for unauthenticated users

    if (!(user && user.id)) {
      // Unauthenticated user - save to localStorage
      let cartKey;
      if (storeId) {
        // Single store mode
        cartKey = `cart_store_${storeId}`;
      } else {
        // Full marketplace mode for customers
        cartKey = 'cart_marketplace';
      }

      console.log('🛍️ PRODUCT CATALOG: Saving cart to localStorage key:', cartKey, 'with items:', newCart?.length || 0);
      localStorage.setItem(cartKey, JSON.stringify(newCart));

      // Dispatch custom event for cross-tab synchronization
      window.dispatchEvent(new CustomEvent('cartUpdated', {
        detail: { cartKey, cart: newCart }
      }));
    }
    // For authenticated users, cart is already saved via API calls
  };

  const addToCart = async (product, quantity = 1) => {
    try {
      // Check for both business owner and customer tokens
      const businessToken = localStorage.getItem('authToken');
      const customerToken = localStorage.getItem('customerToken');
      const token = businessToken || customerToken;

      let serverCartData = null;

      // Call server API for validation and cart persistence
      if (token) {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/cart/add`, {
          productId: product.id,
          quantity,
          storeId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Use cart data from server if available (for authenticated users)
        if (response.data.success && response.data.cart) {
          serverCartData = response.data.cart;
          console.log('🛍️ PRODUCT CATALOG: Using cart data from server:', serverCartData.length, 'items');
        }
      }

      // If we have server cart data, use it; otherwise handle locally
      if (serverCartData !== null) {
        setCart(serverCartData);
        // Also save to localStorage for consistency
        saveCart(serverCartData);
      } else {
        // Handle cart locally for unauthenticated users
        const existingItem = (cart || []).find(item => item.productId === product.id);
        if (existingItem) {
          const newCart = (cart || []).map(item =>
            item.productId === product.id
              ? { ...item, quantity: item.quantity + quantity, total: (item.quantity + quantity) * item.unitPrice }
              : item
          );
          saveCart(newCart);
        } else {
          const newCart = [...(cart || []), {
            productId: product.id,
            product,
            quantity,
            unitPrice: product.price,
            total: quantity * (product.price || 0)
          }];
          saveCart(newCart);
        }
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      // Fallback to local cart management even if server call fails
      const existingItem = (cart || []).find(item => item.productId === product.id);
      if (existingItem) {
        const newCart = (cart || []).map(item =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + quantity, total: (item.quantity + quantity) * item.unitPrice }
            : item
        );
        saveCart(newCart);
      } else {
        const newCart = [...(cart || []), {
          productId: product.id,
          product,
          quantity,
          unitPrice: product.price,
          total: quantity * (product.price || 0)
        }];
        saveCart(newCart);
      }
    }
  };

  const updateCartQuantity = async (productId, newQuantity) => {
    console.log('🛍️ PRODUCT CATALOG: updateCartQuantity called with:', productId, newQuantity);

    if (newQuantity <= 0) {
      console.log('🛍️ PRODUCT CATALOG: Quantity <= 0, calling removeFromCart');
      removeFromCart(productId);
      return;
    }

    // Optimistically update local state first to prevent flashing
    const optimisticCart = (cart || []).map(item =>
      item.productId === productId
        ? { ...item, quantity: newQuantity, total: newQuantity * item.unitPrice }
        : item
    );
    setCart(optimisticCart);

    try {
      // Check for both business owner and customer tokens
      const businessToken = localStorage.getItem('authToken');
      const customerToken = localStorage.getItem('customerToken');
      const token = businessToken || customerToken;

      let serverCartData = null;

      // Call server API for validation and cart persistence
      if (token) {
        console.log('🛍️ PRODUCT CATALOG: Calling server API for quantity update');
        const response = await axios.put(`${process.env.REACT_APP_BACKEND_API_URL}/cart/update`, {
          productId,
          quantity: newQuantity
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        console.log('🛍️ PRODUCT CATALOG: Server response:', response.data);

        // Use cart data from server if available (for authenticated users)
        if (response.data.success) {
          if (response.data.cart && response.data.cart.length > 0) {
            serverCartData = response.data.cart;
            console.log('🛍️ PRODUCT CATALOG: Using updated cart data from server:', serverCartData.length, 'items');
          } else {
            // Server call succeeded but no cart data returned or empty cart - keep optimistic update
            console.log('🛍️ PRODUCT CATALOG: Server call succeeded or returned empty cart, keeping optimistic update');
            saveCart(optimisticCart);
            return;
          }
        } else {
          console.error('🛍️ PRODUCT CATALOG: Server returned error:', response.data.message);
          throw new Error(response.data.message || 'Server error');
        }
      }

      // If we have server cart data, use it; otherwise handle locally
      if (serverCartData !== null) {
        setCart(serverCartData);
        // Also save to localStorage for consistency
        saveCart(serverCartData);
      } else {
        // Handle cart locally for unauthenticated users
        console.log('🛍️ PRODUCT CATALOG: No server data, using optimistic update');
        saveCart(optimisticCart);
      }
    } catch (err) {
      console.error('🛍️ PRODUCT CATALOG: Error updating cart quantity:', err);
      // Revert to previous cart state on error
      setCart(cart);
      // Fallback to local cart management even if server call fails
      const newCart = (cart || []).map(item =>
        item.productId === productId
          ? { ...item, quantity: newQuantity, total: newQuantity * item.unitPrice }
          : item
      );
      saveCart(newCart);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      // Check for both business owner and customer tokens
      const businessToken = localStorage.getItem('authToken');
      const customerToken = localStorage.getItem('customerToken');
      const token = businessToken || customerToken;

      let serverCartData = null;

      // Call server API for validation and cart persistence
      if (token) {
        const response = await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/cart/remove/${productId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Use cart data from server if available (for authenticated users)
        if (response.data.success && response.data.cart) {
          serverCartData = response.data.cart;
          console.log('🛍️ PRODUCT CATALOG: Using updated cart data from server after removal:', serverCartData.length, 'items');
        }
      }

      // If we have server cart data, use it; otherwise handle locally
      if (serverCartData !== null) {
        setCart(serverCartData);
        // Also save to localStorage for consistency
        saveCart(serverCartData);
      } else {
        // Handle cart locally for unauthenticated users
        const newCart = (cart || []).filter(item => item.productId !== productId);
        saveCart(newCart);
      }
    } catch (err) {
      console.error('Error removing from cart:', err);
      // Fallback to local cart management even if server call fails
      const newCart = (cart || []).filter(item => item.productId !== productId);
      saveCart(newCart);
    }
  };

  const getCartItemCount = () => {
    return (cart || []).reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return (cart || []).reduce((total, item) => total + item.total, 0);
  };

  const getCurrentCartKey = () => {
    if (user && user.id) {
      // Authenticated customer - use customer ID for cart persistence
      return `cart_customer_${user.id}`;
    } else if (storeId) {
      // Single store mode for unauthenticated users
      return `cart_store_${storeId}`;
    } else {
      // Full marketplace mode for unauthenticated users
      return 'cart_marketplace';
    }
  };

  const getCategories = () => {
    const categories = [...new Set(products.map(product => product.category || 'General'))];
    return categories.filter(cat => cat && cat.trim() !== '');
  };

  const getCurrentPageProducts = () => {
    const startIndex = (page - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  };

  const ProductCard = ({ product, cartState }) => {
    const cartItem = (cartState || []).find(item => item.productId === product.id);
    const inCart = cartItem ? cartItem.quantity : 0;

    return (
      <Card sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}>
        <CardMedia
          component="div"
          sx={{
            height: 200,
            background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <Inventory sx={{ fontSize: 48, color: '#9e9e9e' }} />
          {product.inStock && (
            <Chip
              label="In Stock"
              color="success"
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8 }}
            />
          )}
        </CardMedia>

        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            {product.name}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
            {product.description}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              SKU: {product.sku}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={product.category}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
              {product.businessName && (
                <Chip
                  label={`🏪 ${product.businessName}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              ₹{product.price ? product.price.toFixed(2) : 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {product.stock || 0} in stock
            </Typography>
          </Box>

          {/* Cart functionality only for customers, not business owners */}
          {!isBusinessOwner && (
            <>
              {inCart > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => updateCartQuantity(product.id, inCart - 1)}
                    color="primary"
                  >
                    <Remove />
                  </IconButton>
                  <Typography sx={{ minWidth: 30, textAlign: 'center' }}>
                    {inCart}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => updateCartQuantity(product.id, inCart + 1)}
                    color="primary"
                    disabled={inCart >= (product.stock || 0)}
                  >
                    <Add />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => addToCart(product)}
                    disabled={!(product.stock > 0)}
                    startIcon={<Add />}
                  >
                    {product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
                  </Button>
                  {storeId && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => window.location.href = `/products`}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Browse All Stores
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}

          {/* Business owner actions */}
          {isBusinessOwner && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                Stock: {product.stock || 0} | Price: ₹{product.price ? product.price.toFixed(2) : 'N/A'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const CartDialog = () => (
    <Dialog open={cartOpen} onClose={() => setCartOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShoppingCart />
        Shopping Cart ({getCartItemCount()} items)
      </DialogTitle>
      <DialogContent>
        {(cart || []).length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Your cart is empty
          </Typography>
        ) : (
          <Stack spacing={2}>
            {(cart || []).map((item) => (
              <Box key={item.productId} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">{item.product.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    ₹{item.unitPrice.toFixed(2)} × {item.quantity}
                  </Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  ₹{item.total.toFixed(2)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeFromCart(item.productId)}
                  color="error"
                >
                  <Remove />
                </IconButton>
              </Box>
            ))}
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 2 }}>
              <Typography variant="h6" sx={{ textAlign: 'right' }}>
                Total: ₹{getCartTotal().toFixed(2)}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCartOpen(false)}>Continue Shopping</Button>
        {(cart || []).length > 0 && (
          <Button
            variant="contained"
            onClick={() => {
              setCartOpen(false);
              // Navigate to checkout - use different params for different modes
              // Build checkout URL with correct parameters based on current cart mode
              let checkoutUrl = '/checkout';
              if (storeId) {
                // Single store checkout
                checkoutUrl = `/checkout?storeId=${storeId}`;
              } else if (user && user.id) {
                // Authenticated customer checkout - cart is already user-specific
                checkoutUrl = '/checkout?mode=customer';
              } else {
                // Unauthenticated marketplace checkout
                checkoutUrl = '/checkout?mode=marketplace';
              }
              console.log('🛍️ PRODUCT CATALOG: Navigating to checkout:', checkoutUrl);
              window.location.href = checkoutUrl;
            }}
          >
            Proceed to Checkout
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Product Catalog</Typography>
        <Grid container spacing={3}>
          {[...Array(8)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" height={30} />
                  <Skeleton variant="text" height={20} />
                  <Skeleton variant="text" height={20} />
                  <Skeleton variant="rectangular" height={36} sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchProducts}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {storeId ? 'Store Products' : (isBusinessOwner ? 'My Products' : (!user ? 'Marketplace' : 'Product Catalog'))}
          </Typography>
          {storeId && (
            <Typography variant="body2" color="text.secondary">
              Browse products from this store
            </Typography>
          )}
          {isBusinessOwner && !storeId && (
            <Typography variant="body2" color="text.secondary">
              Manage your product inventory
            </Typography>
          )}
          {!storeId && !user && (
            <Typography variant="body2" color="text.secondary">
              Discover products from all stores
            </Typography>
          )}
        </Box>

        {/* Cart Button - Only show for customers, not business owners */}
        {!isBusinessOwner && (
          <Fab
            color="primary"
            onClick={() => setCartOpen(true)}
            sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}
          >
            <Badge badgeContent={getCartItemCount()} color="error">
              <ShoppingCart />
            </Badge>
          </Fab>
        )}
      </Box>

      {/* Filters and Search */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            label="Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {getCategories().map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            label="Sort By"
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="price">Price</MenuItem>
            <MenuItem value="stock">Stock</MenuItem>
            {!storeId && !isBusinessOwner && (
              <>
                <MenuItem value="trending">Trending</MenuItem>
                <MenuItem value="rating">Top Rated</MenuItem>
                <MenuItem value="recent">Recently Added</MenuItem>
              </>
            )}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          startIcon={<Sort />}
        >
          {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
        </Button>
      </Box>

      {/* Results Summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Showing {getCurrentPageProducts().length} of {filteredProducts.length} products
      </Typography>

      {/* Products Grid */}
      <Grid container spacing={3}>
        {getCurrentPageProducts().map((product) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`${product.id}-${getCartItemCount()}`}>
            <ProductCard product={product} cartState={cart} />
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Empty State */}
      {filteredProducts.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Inventory sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filters
          </Typography>
        </Box>
      )}

      {/* Cart Dialog - Only show for customers, not business owners */}
      {!isBusinessOwner && <CartDialog />}
    </Box>
  );
};

export default ProductCatalog;